<template>
  <div class="search-area">
    <div class="search-area__fields">
      <div
        v-for="field in searchFields"
        :key="field.key"
        class="search-area__field"
      >
        <!-- 기간 선택 -->
        <template v-if="field.type === 'period'">
          <div class="search-area__datepicker" ref="datepickerContainer">
            <div class="p-input-group">
              <i class="material-icons-round search-area__field-icon">date_range</i>
              <input
                type="text"
                class="p-input"
                :value="dateRangeText"
                @click="toggleDatepicker"
                readonly
                :placeholder="field.placeholder"
              />
            </div>
            <div v-show="showDatepicker" class="search-area__datepicker-popup">
              <VueDatePicker
                v-model="dateRange"
                range
                @update:model-value="handleDateChange"
                @closed="showDatepicker = false"
                format="yyyy-MM-dd"
                :min-date="new Date()"
                :max-date="new Date(new Date().getFullYear() + 1, 11, 31)"
                week-start="1"
                locale="ko"
                auto-apply
                inline
              />
            </div>
          </div>
        </template>

        <!-- 셀렉트 박스 -->
        <template v-else-if="field.type === 'select'">
          <div class="p-input-group">
            <i class="material-icons-round search-area__field-icon">filter_alt</i>
            <select
              class="p-input"
              v-model="searchValues[field.key]"
              @change="handleSearch"
            >
              <option value="">{{ field.placeholder }}</option>
              <option
                v-for="option in field.options"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </div>
        </template>

        <!-- 텍스트 입력 (기본) -->
        <template v-else>
          <div class="p-input-group">
            <i class="material-icons-round search-area__field-icon">search</i>
            <input
              type="text"
              class="p-input"
              v-model="searchValues[field.key]"
              @keyup.enter="handleSearch"
              :placeholder="field.placeholder"
            />
          </div>
        </template>
      </div>
    </div>

    <div class="search-area__actions">
      <ABtn
        v-if="showSearchButton"
        kind="primary"
        icon="search"
        @click="handleSearch"
      >
        {{ $t('btn.16') }}
      </ABtn>
      <ABtn
        v-if="showDownloadButton"
        kind="ghost"
        icon="download"
        @click="handleDownload"
      >
        {{ $t('btn.19') }}
      </ABtn>
    </div>
  </div>
</template>

<script>
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import ABtn from '@/components/Pineapple/ABtn.vue';

export default {
  name: 'SearchArea',
  components: {
    VueDatePicker,
    ABtn,
  },
  props: {
    searchFields: {
      type: Array,
      required: true,
    },
    showSearchButton: {
      type: Boolean,
      default: true,
    },
    showDownloadButton: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['search', 'reset', 'download'],
  data() {
    return {
      searchValues: {},
      dateRange: null,
      showDatepicker: false,
      datepickerContainer: null,
      dateRangeText: '',
    };
  },
  watch: {
    dateRange: {
      handler(newValue) {
        if (!newValue) {
          this.dateRangeText = '';
          return;
        }
        if (Array.isArray(newValue) && newValue.length === 2) {
          const start = newValue[0] ? this.formatDate(newValue[0]) : '';
          const end = newValue[1] ? this.formatDate(newValue[1]) : '';
          this.dateRangeText = start && end ? `${start} ~ ${end}` : '';
        }
      },
      immediate: true,
    },
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
    if (this.$refs.datepickerContainer && this.$refs.datepickerContainer.length > 0) {
      this.datepickerContainer = this.$refs.datepickerContainer[0];
    }
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  created() {
    this.searchFields.forEach(field => {
      this.searchValues[field.key] = '';
    });
  },
  methods: {
    toggleDatepicker(event) {
      event.stopPropagation();
      this.showDatepicker = !this.showDatepicker;
    },
    handleClickOutside(event) {
      if (this.datepickerContainer && !this.datepickerContainer.contains(event.target)) {
        this.showDatepicker = false;
      }
    },
    formatDate(date) {
      if (!date) return '';
      const d = new Date(date);
      return d.toISOString().split('T')[0];
    },
    handleDateChange(dates) {
      if (Array.isArray(dates) && dates.length === 2 && dates[0] && dates[1]) {
        const start = this.formatDate(dates[0]);
        const end = this.formatDate(dates[1]);
        this.searchValues.period = `${start} ~ ${end}`;
        this.showDatepicker = false;
      } else {
        this.searchValues.period = '';
      }
    },
    handleSearch() {
      const searchData = { ...this.searchValues };
      if (searchData.period) {
        const [start_date, end_date] = searchData.period.split('~').map(date => date.trim());
        searchData.start_date = start_date;
        searchData.end_date = end_date;
        delete searchData.period;
      }
      this.$emit('search', searchData);
    },
    handleDownload() {
      this.$emit('download', this.searchValues);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.search-area {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  flex-wrap: wrap;
}

.search-area__fields {
  flex: 1;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 10px;
  min-width: 0;
}

.search-area__field {
  min-width: 0;
}

// Group: icon prefix + input
.p-input-group {
  position: relative;
  display: flex;
  align-items: center;
  background: $p-panel;
  border: 1px solid $p-line;
  border-radius: $p-radius-sm;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus-within {
    border-color: $p-point-deep;
    box-shadow: 0 0 0 3px $p-point-glow;
  }

  .p-input {
    border: 0 !important;
    background: transparent !important;
    box-shadow: none !important;
    height: 38px;
    padding: 0 12px 0 36px;
    width: 100%;

    &:focus {
      box-shadow: none !important;
      border: 0 !important;
    }
  }

  select.p-input {
    appearance: none;
    background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%231F1B14' stroke-width='2.2' stroke-linecap='round'%3e%3cpolyline points='6 9 12 15 18 9'/%3e%3c/svg%3e");
    background-repeat: no-repeat;
    background-position: right 10px center;
    background-size: 12px 12px;
    padding-right: 30px;
  }
}

.search-area__field-icon {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 18px !important;
  color: $p-ink-soft;
  pointer-events: none;
  z-index: 1;
}

.search-area__actions {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

// Datepicker
.search-area__datepicker {
  position: relative;
  width: 100%;
}

.search-area__datepicker-popup {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 1000;
  background: $p-panel;
  border: 1px solid $p-line;
  border-radius: $p-radius;
  box-shadow: 0 12px 32px rgba(31, 27, 20, 0.18);
  padding: 8px;
}

// VueDatePicker — Pineapple yellow tokens (was green/pink default)
:deep(.dp__theme_light) {
  --dp-primary-color: #{$p-point-deep};
  --dp-primary-text-color: #{$p-ink};
  --dp-primary-disabled-color: #{$p-line};
  --dp-secondary-color: #{$p-panel-alt};
  --dp-text-color: #{$p-ink};
  --dp-border-color: #{$p-line};
  --dp-border-color-hover: #{$p-line-hard};
  --dp-icon-color: #{$p-ink-mid};
  --dp-success-color: #{$p-ok};
  --dp-hover-color: #{$p-point-soft};
  --dp-hover-text-color: #{$p-ink};
  --dp-border-radius: #{$p-radius-sm};
  --dp-cell-border-radius: 6px;
  font-family: $p-font-body;
  border: none;
}

:deep(.dp__menu) {
  border: 0 !important;
  box-shadow: none !important;
}

:deep(.dp__calendar_header) {
  font-weight: 700;
  color: $p-ink-mid;
}

:deep(.dp__calendar_item),
:deep(.dp__cell_inner) {
  font-family: $p-font-body;
}

:deep(.dp__active_date),
:deep(.dp__range_start),
:deep(.dp__range_end) {
  background: $p-point !important;
  color: $p-ink !important;
  font-weight: 700;
}

:deep(.dp__range_between) {
  background: $p-point-soft !important;
  color: $p-ink !important;
}

:deep(.dp__today) {
  border: 1px solid $p-point-deep !important;
}

@media (max-width: 768px) {
  .search-area { flex-direction: column; align-items: stretch; }
  .search-area__actions { justify-content: flex-end; }
}
</style>
